import { useEffect, useMemo, useState } from "react";
import api, { inr } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Search, Download, Calendar, RefreshCw } from "lucide-react";

const TYPE_LABEL = {
  direct_referral:          "Direct Referral",
  binary_matching:          "Pair Matching",
  monthly_bonus:            "Monthly Bonus",
  startup_income:           "Startup Income",
  booster:                  "Booster",
  self_retail:              "Self Retail",
  reward:                   "Rank Reward",
  direct_referral_expired:  "Direct Ref (expired)",
  direct_referral_expired_resolved: "Direct Ref (retro)",
};

/**
 * Admin > Sales Ledger (datewise all)
 * ----------------------------------------------
 * Chronological list of every incomes row across every IBA. Server side we
 * expose /admin/sales-ledger which just streams `incomes` sorted by
 * created_at desc with a `from`/`to` date filter + optional user_code /
 * type filter. Grand totals per type are computed in-browser for speed.
 */
export default function AdminSalesLedger() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");

  const refetch = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to)   params.append("to",   to);
    if (type) params.append("type", type);
    api.get(`/admin/sales-ledger?${params}`)
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, [from, to, type]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.user_code || "").toLowerCase().includes(needle) ||
      (r.user_name || "").toLowerCase().includes(needle) ||
      (r.description || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const byType = useMemo(() => {
    const acc = {};
    for (const r of filtered) {
      acc[r.type] = (acc[r.type] || 0) + Number(r.amount || 0);
    }
    return acc;
  }, [filtered]);

  const grandTotal = useMemo(
    () => Object.values(byType).reduce((a, b) => a + b, 0),
    [byType],
  );

  const exportCsv = () => {
    const head = ["Date", "User Code", "Name", "Type", "Description", "Amount (₹)"];
    const csvRows = [
      head.join(","),
      ...filtered.map((r) => [
        r.created_at?.slice(0, 10) || "", r.user_code, r.user_name,
        TYPE_LABEL[r.type] || r.type, r.description, r.amount,
      ].map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `samara-sales-ledger-${from || "all"}_to_${to || "now"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout
      title="Sales Ledger"
      subtitle={`${filtered.length} entries · ₹${Math.round(grandTotal).toLocaleString("en-IN")} total`}
      actions={
        <button
          onClick={exportCsv}
          data-testid="ledger-export"
          disabled={!filtered.length}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-navy/15 rounded-sm hover:bg-cream/40 text-navy/70 disabled:opacity-50"
        >
          <Download size={12} /> Export CSV
        </button>
      }
    >
      {/* Date + type filters */}
      <div className="bg-white border border-border p-4 rounded-sm mb-4 flex flex-col md:flex-row gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Calendar size={14} className="text-navy/40" />
          <input
            type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            data-testid="ledger-from"
            className="border border-border px-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none"
          />
          <span className="text-navy/40 text-xs">to</span>
          <input
            type="date" value={to} onChange={(e) => setTo(e.target.value)}
            data-testid="ledger-to"
            className="border border-border px-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none"
          />
        </div>
        <select
          value={type} onChange={(e) => setType(e.target.value)}
          data-testid="ledger-type"
          className="border border-border px-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none"
        >
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search code, name, description…"
            data-testid="ledger-search"
            className="w-full border border-border pl-9 pr-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none"
          />
        </div>
        <button
          onClick={refetch}
          data-testid="ledger-refresh"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-navy/15 rounded-sm hover:bg-cream/40 text-navy/70"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Totals-by-type strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4" data-testid="ledger-totals">
        {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} className="bg-white border border-border rounded-sm px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-navy/50">{TYPE_LABEL[k] || k}</div>
            <div className="tabular-nums font-serif text-lg text-navy mt-0.5">{inr(v)}</div>
          </div>
        ))}
      </div>

      {/* Ledger table */}
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="ledger-table">
          <thead className="bg-navy text-white">
            <tr>
              {["Date", "Code", "Name", "Type", "Description", "Amount"].map((h) => (
                <th key={h} className={`px-4 py-3 text-xs uppercase tracking-widest font-medium ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-navy/50 text-sm">
                  {loading ? "Loading…" : "No entries in this range."}
                </td>
              </tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`ledger-row-${r.id}`} className="border-b border-border last:border-0 hover:bg-cream/40">
                <td className="px-4 py-2 text-navy/50 text-xs tabular-nums whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2 font-mono text-navy text-xs">{r.user_code}</td>
                <td className="px-4 py-2 text-navy/70 text-xs">{r.user_name}</td>
                <td className="px-4 py-2 text-[10px] uppercase tracking-widest text-royal">{TYPE_LABEL[r.type] || r.type}</td>
                <td className="px-4 py-2 text-navy/70 text-xs">{r.description}</td>
                <td className={`px-4 py-2 text-right tabular-nums text-sm font-semibold ${Number(r.amount || 0) > 0 ? "text-olive" : "text-navy/40"}`}>
                  +{inr(Number(r.amount || 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
